function segment(value: string) {
  const normalized = value.trim()
  if (!normalized) {
    throw new Error('O identificador da rota administrativa é obrigatório')
  }
  if (normalized.includes('/') || normalized.includes('\\')) {
    throw new Error('O identificador da rota administrativa contém separador inválido')
  }
  return encodeURIComponent(normalized)
}

export const adminRoutes = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    me: '/auth/me',
    csrf: '/auth/csrf',
  },
  dashboard: {
    overview: '/dashboard/overview',
    summary: '/dashboard/summary',
    timeseries: '/dashboard/timeseries',
    providers: '/dashboard/providers',
    conversions: '/dashboard/conversions',
  },
  tenants: {
    collection: '/tenants',
    item: (tenantId: string) => `/tenants/${segment(tenantId)}`,
    activate: (tenantId: string) => `/tenants/${segment(tenantId)}/activate`,
    suspend: (tenantId: string) => `/tenants/${segment(tenantId)}/suspend`,
    environments: (tenantId: string) => `/tenants/${segment(tenantId)}/environments`,
    activationReadiness: (tenantId: string) => `/tenants/${segment(tenantId)}/activation-readiness`,
    platformReadiness: (tenantId: string) => `/tenants/${segment(tenantId)}/platform-readiness`,
    activatePlatform: (tenantId: string) => `/tenants/${segment(tenantId)}/activate-platform`,
  },
  environments: {
    collection: '/environments',
    item: (environmentId: string) => `/environments/${segment(environmentId)}`,
    makeDefault: (environmentId: string) => `/environments/${segment(environmentId)}/make-default`,
    activate: (environmentId: string) => `/environments/${segment(environmentId)}/activate`,
    disable: (environmentId: string) => `/environments/${segment(environmentId)}/disable`,
  },
  flowBuilder: {
    collection: '/flow-builder',
    item: (flowId: string) => `/flow-builder/${segment(flowId)}`,
    validate: (flowId: string) => `/flow-builder/${segment(flowId)}/validate`,
    execute: (flowId: string) => `/flow-builder/${segment(flowId)}/execute`,
  },
  channels: {
    definitions: '/channels/definitions',
    instances: '/channels/instances',
    instance: (instanceId: string) => `/channels/instances/${segment(instanceId)}`,
    synchronizeMetaConfiguration: (instanceId: string) => `/channels/instances/${segment(instanceId)}/meta-configuration/synchronize`,
    clone: (instanceId: string) => `/channels/instances/${segment(instanceId)}/clone`,
    versions: (instanceId: string) => `/channels/instances/${segment(instanceId)}/versions`,
    rollback: (instanceId: string) => `/channels/instances/${segment(instanceId)}/rollback`,
    credentials: (instanceId: string) => `/channels/instances/${segment(instanceId)}/credentials`,
    credential: (instanceId: string, credentialKey: string) => `/channels/instances/${segment(instanceId)}/credentials/${segment(credentialKey)}`,
    readiness: (instanceId: string) => `/channels/instances/${segment(instanceId)}/readiness`,
    runReadiness: (instanceId: string) => `/channels/instances/${segment(instanceId)}/readiness/run`,
    test: (instanceId: string) => `/channels/instances/${segment(instanceId)}/test`,
    activate: (instanceId: string) => `/channels/instances/${segment(instanceId)}/activate`,
    pause: (instanceId: string) => `/channels/instances/${segment(instanceId)}/pause`,
    disable: (instanceId: string) => `/channels/instances/${segment(instanceId)}/disable`,
    webhook: (instanceId: string) => `/channels/instances/${segment(instanceId)}/webhook`,
    webhookDeliveries: (instanceId: string) => `/channels/instances/${segment(instanceId)}/webhook/deliveries`,
    rotateWebhookVerifyToken: (instanceId: string) => `/channels/instances/${segment(instanceId)}/webhook/rotate-verify-token`,
  },
  secrets: {
    collection: '/secrets',
    rotate: (secretId: string) => `/secrets/${segment(secretId)}/rotate`,
    revoke: (secretId: string) => `/secrets/${segment(secretId)}/revoke`,
    validate: (secretId: string) => `/secrets/${segment(secretId)}/validate`,
  },
  runtimeConfig: {
    current: '/runtime-config/current',
    history: '/runtime-config/history',
    rebuild: '/runtime-config/rebuild',
    rollback: '/runtime-config/rollback',
  },
  users: {
    collection: '/users',
    item: (userId: string) => `/users/${segment(userId)}`,
    block: (userId: string) => `/users/${segment(userId)}/block`,
    revokeSessions: (userId: string) => `/users/${segment(userId)}/revoke-sessions`,
  },
  roles: {
    collection: '/roles',
    permissions: '/roles/permissions',
    item: (roleId: string) => `/roles/${segment(roleId)}`,
  },
  theBrain: {
    overview: '/the-brain/overview',
    catalog: '/the-brain/catalog',
    contexts: '/the-brain/contexts',
    context: (contextId: string) => `/the-brain/contexts/${segment(contextId)}`,
    publish: (contextId: string) => `/the-brain/contexts/${segment(contextId)}/publish`,
    pause: (contextId: string) => `/the-brain/contexts/${segment(contextId)}/pause`,
    simulateDraft: (contextId: string) => `/the-brain/contexts/${segment(contextId)}/simulate`,
    versions: (contextId: string) => `/the-brain/contexts/${segment(contextId)}/versions`,
    restore: (contextId: string, versionId: string) => `/the-brain/contexts/${segment(contextId)}/versions/${segment(versionId)}/restore`,
    entries: (contextId: string) => `/the-brain/contexts/${segment(contextId)}/entries`,
    entry: (entryId: string) => `/the-brain/entries/${segment(entryId)}`,
    rules: (contextId: string) => `/the-brain/contexts/${segment(contextId)}/rules`,
    rule: (ruleId: string) => `/the-brain/rules/${segment(ruleId)}`,
    simulate: '/the-brain/simulate',
  },
  conversationBrain: {
    policy: '/conversation-brain/policy',
    agentOptions: '/conversation-brain/agent-options',
    synonyms: '/conversation-brain/synonyms',
    synonym: (synonymId: string) => `/conversation-brain/synonyms/${segment(synonymId)}`,
    test: '/conversation-brain/test',
    reindex: '/conversation-brain/reindex',
    reindexJob: (jobId: string) => `/conversation-brain/reindex/${segment(jobId)}`,
    decisions: '/conversation-brain/decisions',
    decision: (decisionId: string) => `/conversation-brain/decisions/${segment(decisionId)}`,
    metrics: '/conversation-brain/metrics',
    versions: '/conversation-brain/versions',
    activateVersion: (versionId: string) => `/conversation-brain/versions/${segment(versionId)}/activate`,
    rollbackVersion: (versionId: string) => `/conversation-brain/versions/${segment(versionId)}/rollback`,
  },
  brain: {
    overview: '/brain/overview',
    limits: '/brain/limits',
    agents: '/brain/agents',
    agentProfiles: '/brain/agent-profiles',
    agentProfile: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}`,
    agentProfileDraft: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}/draft`,
    validateAgentProfile: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}/validate`,
    testAgentProfile: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}/test`,
    publishAgentProfile: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}/publish`,
    rollbackAgentProfile: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}/rollback`,
    agentProfileVersions: (agentId: string) => `/brain/agent-profiles/${segment(agentId)}/versions`,
    agent: (agentId: string) => `/brain/agents/${segment(agentId)}`,
    activateAgent: (agentId: string) => `/brain/agents/${segment(agentId)}/activate`,
    pauseAgent: (agentId: string) => `/brain/agents/${segment(agentId)}/pause`,
    validateAgent: (agentId: string) => `/brain/agents/${segment(agentId)}/validate`,
    testAgent: (agentId: string) => `/brain/agents/${segment(agentId)}/test`,
    cloneAgent: (agentId: string) => `/brain/agents/${segment(agentId)}/clone`,
    resources: (kind: string) => `/brain/${segment(kind)}`,
    resource: (kind: string, resourceId: string) => `/brain/${segment(kind)}/${segment(resourceId)}`,
    validateResource: (kind: string, resourceId: string) => `/brain/${segment(kind)}/${segment(resourceId)}/validate`,
    cloneResource: (kind: string, resourceId: string) => `/brain/${segment(kind)}/${segment(resourceId)}/clone`,
    activateResource: (kind: string, resourceId: string) => `/brain/${segment(kind)}/${segment(resourceId)}/activate`,
    deactivateResource: (kind: string, resourceId: string) => `/brain/${segment(kind)}/${segment(resourceId)}/deactivate`,
    prompts: '/brain/prompts',
    prompt: (promptId: string) => `/brain/prompts/${segment(promptId)}`,
    promptClone: (promptId: string) => `/brain/prompts/${segment(promptId)}/clone`,
    promptVersions: (promptId: string) => `/brain/prompts/${segment(promptId)}/versions`,
    promptVersionPublish: (promptId: string, versionId: string) => `/brain/prompts/${segment(promptId)}/versions/${segment(versionId)}/publish`,
    promptUnpublish: (promptId: string) => `/brain/prompts/${segment(promptId)}/unpublish`,
    promptVersionRestore: (promptId: string, versionId: string) => `/brain/prompts/${segment(promptId)}/versions/${segment(versionId)}/restore`,
    promptTest: (promptId: string) => `/brain/prompts/${segment(promptId)}/test`,
    promptBindings: (promptId: string) => `/brain/prompts/${segment(promptId)}/bindings`,
    promptActivate: (promptId: string) => `/brain/prompts/${segment(promptId)}/activate`,
    promptDeactivate: (promptId: string) => `/brain/prompts/${segment(promptId)}/deactivate`,
    agentTemplates: '/brain/agent-templates',
    agentTemplate: (templateId: string) => `/brain/agent-templates/${segment(templateId)}`,
    agentTemplateClone: (templateId: string) => `/brain/agent-templates/${segment(templateId)}/clone`,
    agentTemplateActivate: (templateId: string) => `/brain/agent-templates/${segment(templateId)}/activate`,
    agentTemplateDeactivate: (templateId: string) => `/brain/agent-templates/${segment(templateId)}/deactivate`,
    cronjobs: '/brain/cronjobs',
    cronjob: (cronjobId: string) => `/brain/cronjobs/${segment(cronjobId)}`,
    runCronjob: (cronjobId: string) => `/brain/cronjobs/${segment(cronjobId)}/run`,
    pauseCronjob: (cronjobId: string) => `/brain/cronjobs/${segment(cronjobId)}/pause`,
    resumeCronjob: (cronjobId: string) => `/brain/cronjobs/${segment(cronjobId)}/resume`,
    cronjobExecutions: (cronjobId: string) => `/brain/cronjobs/${segment(cronjobId)}/executions`,
    executions: '/brain/observability/executions',
    execution: (executionId: string) => `/brain/observability/executions/${segment(executionId)}`,
    events: '/brain/observability/events',
    metrics: '/brain/observability/metrics',
    stream: '/brain/observability/stream',
    approvals: '/brain/approvals',
    approval: (approvalId: string) => `/brain/approvals/${segment(approvalId)}`,
    approve: (approvalId: string) => `/brain/approvals/${segment(approvalId)}/approve`,
    reject: (approvalId: string) => `/brain/approvals/${segment(approvalId)}/reject`,
    knowledgeSources: '/brain/knowledge/sources',
    knowledgeSource: (sourceId: string) => `/brain/knowledge/sources/${segment(sourceId)}`,
    knowledgeIngest: '/brain/knowledge/ingest',
    knowledgeItems: '/brain/knowledge/items',
    knowledgeItem: (itemId: string) => `/brain/knowledge/items/${segment(itemId)}`,
    feedback: '/brain/learning/feedback',
    learningMetrics: '/brain/learning/metrics',
  },
  catalog: {
    products: '/catalog/products',
    product: (productId: string) => `/catalog/products/${segment(productId)}`,
    assets: '/catalog/assets',
    asset: (assetId: string) => `/catalog/assets/${segment(assetId)}`,
    attachPrimaryAsset: (productId: string) => `/catalog/products/${segment(productId)}/primary-asset`,
    detachPrimaryAsset: (productId: string) => `/catalog/products/${segment(productId)}/primary-asset`,
    testTryOn: (productId: string) => `/catalog/products/${segment(productId)}/test-try-on`,
  },
  agents: {
    collection: '/agents',
    eligible: '/agents/eligible',
    item: (agentId: string) => `/agents/${segment(agentId)}`,
    versions: (agentId: string) => `/agents/${segment(agentId)}/versions`,
    activate: (agentId: string) => `/agents/${segment(agentId)}/activate`,
    rollback: (agentId: string) => `/agents/${segment(agentId)}/rollback`,
    publish: (agentId: string) => `/agents/${segment(agentId)}/publish`,
    unpublish: (agentId: string) => `/agents/${segment(agentId)}/unpublish`,
    usage: (agentId: string) => `/agents/${segment(agentId)}/usage`,
    test: (agentId: string) => `/agents/${segment(agentId)}/test`,
    validateItem: (agentId: string) => `/agents/${segment(agentId)}/validate`,
    validate: '/agents/validate',
    validationRules: '/agents/validation-rules',
  },
  products: {
    collection: '/products',
    item: (productId: string) => `/products/${segment(productId)}`,
  },
  conversationConfigs: {
    collection: '/conversation-configs',
    item: (configId: string) => `/conversation-configs/${segment(configId)}`,
    versions: (configId: string) => `/conversation-configs/${segment(configId)}/versions`,
    draft: (configId: string) => `/conversation-configs/${segment(configId)}/draft`,
    diff: (configId: string) => `/conversation-configs/${segment(configId)}/diff`,
    validate: (configId: string) => `/conversation-configs/${segment(configId)}/validate`,
    publish: (configId: string) => `/conversation-configs/${segment(configId)}/publish`,
    unpublish: (configId: string) => `/conversation-configs/${segment(configId)}/unpublish`,
    rollback: (configId: string) => `/conversation-configs/${segment(configId)}/rollback`,
  },
  productConversationBindings: {
    collection: '/product-conversation-bindings',
    item: (bindingId: string) => `/product-conversation-bindings/${segment(bindingId)}`,
    validate: (bindingId: string) => `/product-conversation-bindings/${segment(bindingId)}/validate`,
    publish: (bindingId: string) => `/product-conversation-bindings/${segment(bindingId)}/publish`,
    unpublish: (bindingId: string) => `/product-conversation-bindings/${segment(bindingId)}/unpublish`,
  },
  conversationEngine: {
    simulate: '/conversation-engine/simulate',
    workspace: (productId: string) => `/conversation-engine/products/${segment(productId)}/workspace`,
    draft: (productId: string) => `/conversation-engine/products/${segment(productId)}/draft`,
    publication: (productId: string) => `/conversation-engine/products/${segment(productId)}/publication`,
  },
  tokens: {
    overview: '/tokens/overview',
    usage: '/tokens/usage',
    costs: '/tokens/costs',
    budgets: '/tokens/budgets',
    budget: (budgetId: string) => `/tokens/budgets/${segment(budgetId)}`,
    projection: '/tokens/projection',
  },
  providers: {
    collection: '/providers',
    item: (providerId: string) => `/providers/${segment(providerId)}`,
    test: (providerId: string) => `/providers/${segment(providerId)}/test`,
    activate: (providerId: string) => `/providers/${segment(providerId)}/activate`,
    disable: (providerId: string) => `/providers/${segment(providerId)}/disable`,
    models: (providerId: string) => `/providers/${segment(providerId)}/models`,
    model: (providerId: string, modelId: string) => `/providers/${segment(providerId)}/models/${segment(modelId)}`,
    modelStatus: (providerId: string, modelId: string) => `/providers/${segment(providerId)}/models/${segment(modelId)}/status`,
    modelDependencies: (providerId: string, modelId: string) => `/providers/${segment(providerId)}/models/${segment(modelId)}/dependencies`,
    validateModel: (providerId: string, modelId: string) => `/providers/${segment(providerId)}/models/${segment(modelId)}/validate`,
    modelAgents: (providerId: string, modelId: string) => `/providers/${segment(providerId)}/models/${segment(modelId)}/agents`,
    capabilities: (providerId: string, modelId: string) => `/providers/${segment(providerId)}/models/${segment(modelId)}/capabilities`,
  },
  apiRoutes: {
    collection: '/routes',
    item: (routeId: string) => `/routes/${segment(routeId)}`,
    test: (routeId: string) => `/routes/${segment(routeId)}/test`,
    activate: (routeId: string) => `/routes/${segment(routeId)}/activate`,
    disable: (routeId: string) => `/routes/${segment(routeId)}/disable`,
  },
  resourceLifecycle: {
    deleted: '/resource-lifecycle/deleted',
    item: (resourceKey: string, resourceId: string) => `/resource-lifecycle/${segment(resourceKey)}/${segment(resourceId)}`,
    dependencies: (resourceKey: string, resourceId: string, operation?: 'DELETE' | 'PURGE') => `/resource-lifecycle/${segment(resourceKey)}/${segment(resourceId)}/dependencies${operation === 'PURGE' ? '?operation=PURGE' : ''}`,
    restore: (resourceKey: string, resourceId: string) => `/resource-lifecycle/${segment(resourceKey)}/${segment(resourceId)}/restore`,
    purge: (resourceKey: string, resourceId: string) => `/resource-lifecycle/${segment(resourceKey)}/${segment(resourceId)}/purge`,
  },
  formConfigurationProfiles: {
    collection: '/form-configuration-profiles',
    item: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}`,
    options: '/form-configuration-profiles/options',
    definitions: '/form-configuration-profiles/definitions',
    fieldDependencies: '/form-configuration-profiles/dependencies',
    restore: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/restore`,
    purge: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/purge`,
    clone: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/clone`,
    validate: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/validate`,
    preview: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/preview`,
    setDefault: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/set-default`,
    dependencies: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/dependencies`,
    history: (profileId: string) => `/form-configuration-profiles/${segment(profileId)}/history`,
  },
  configurations: {
    collection: '/configurations',
    item: (configurationId: string) => `/configurations/${segment(configurationId)}`,
    versions: (configurationId: string) => `/configurations/${segment(configurationId)}/versions`,
    rollback: (configurationId: string) => `/configurations/${segment(configurationId)}/rollback`,
  },
  featureFlags: {
    collection: '/feature-flags',
    item: (flagId: string) => `/feature-flags/${segment(flagId)}`,
    activate: (flagId: string) => `/feature-flags/${segment(flagId)}/activate`,
    disable: (flagId: string) => `/feature-flags/${segment(flagId)}/disable`,
  },
  tryOn: {
    sessions: '/try-on/sessions',
    session: (sessionId: string) => `/try-on/sessions/${segment(sessionId)}`,
    retry: (sessionId: string) => `/try-on/sessions/${segment(sessionId)}/retry`,
    cancel: (sessionId: string) => `/try-on/sessions/${segment(sessionId)}/cancel`,
    resend: (sessionId: string) => `/try-on/sessions/${segment(sessionId)}/resend`,
    deleteAssets: (sessionId: string) => `/try-on/sessions/${segment(sessionId)}/delete-assets`,
  },
  postTryOn: {
    settings: '/try-on/post-experience/settings',
    journeys: '/try-on/post-experience/journeys',
    journey: (journeyId: string) => `/try-on/post-experience/journeys/${segment(journeyId)}`,
    resendResult: (journeyId: string) => `/try-on/post-experience/journeys/${segment(journeyId)}/resend-result`,
    resendCheckout: (journeyId: string) => `/try-on/post-experience/journeys/${segment(journeyId)}/resend-checkout`,
    sendFallback: (journeyId: string) => `/try-on/post-experience/journeys/${segment(journeyId)}/send-fallback`,
    close: (journeyId: string) => `/try-on/post-experience/journeys/${segment(journeyId)}/close`,
  },
  apparelTryOn: {
    garments: '/try-on/apparel/garments',
    garment: (garmentId: string) => `/try-on/apparel/garments/${segment(garmentId)}`,
    garmentImage: (garmentId: string) => `/try-on/apparel/garments/${segment(garmentId)}/image`,
    validateGarment: (garmentId: string) => `/try-on/apparel/garments/${segment(garmentId)}/validate`,
    activateGarment: (garmentId: string) => `/try-on/apparel/garments/${segment(garmentId)}/activate`,
    disableGarment: (garmentId: string) => `/try-on/apparel/garments/${segment(garmentId)}/disable`,
    providerCatalog: '/try-on/apparel/provider-catalog',
    eligibleProviderSecrets: '/try-on/apparel/eligible-secrets',
    providers: '/try-on/apparel/providers',
    provider: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}`,
    providerHealth: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}/health-check`,
    providerTest: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}/test-generation`,
    providerDiagnostics: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}/diagnostics`,
    activateProvider: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}/activate`,
    pauseProvider: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}/pause`,
    disableProvider: (providerId: string) => `/try-on/apparel/providers/${segment(providerId)}/disable`,
    generations: '/try-on/apparel/generations',
    generation: (jobId: string) => `/try-on/apparel/generations/${segment(jobId)}`,
    cancelGeneration: (jobId: string) => `/try-on/apparel/generations/${segment(jobId)}/cancel`,
    retryGeneration: (jobId: string) => `/try-on/apparel/generations/${segment(jobId)}/retry`,
    metrics: '/try-on/apparel/metrics',
    settings: '/try-on/apparel/settings',
  },
  consents: {
    collection: '/consents',
    revoke: (consentId: string) => `/consents/${segment(consentId)}/revoke`,
  },

  publicSettings: {
    collection: '/public-settings',
  },
  apiKeys: {
    collection: '/api-keys',
    revoke: (keyId: string) => `/api-keys/${segment(keyId)}/revoke`,
    rotate: (keyId: string) => `/api-keys/${segment(keyId)}/rotate`,
  },
  allowedOrigins: {
    collection: '/allowed-origins',
    item: (originId: string) => `/allowed-origins/${segment(originId)}`,
  },
  integrations: {
    status: '/integrations/status',
  },
  tryOnAssets: {
    collection: '/try-on-assets',
    item: (assetId: string) => `/try-on-assets/${segment(assetId)}`,
    upload: '/try-on-assets/upload',
  },
  prospectSDR: {
    leads: '/prospect-sdr/leads',
    agents: '/prospect-sdr/agents',
    playbooks: '/prospect-sdr/playbooks',
    qualificationFields: '/prospect-sdr/qualification/fields',
    qualificationQuestions: '/prospect-sdr/qualification/questions',
    scoringModels: '/prospect-sdr/scoring/models',
    followups: '/prospect-sdr/followups',
    meetings: '/prospect-sdr/meetings',
    calendarConnections: '/prospect-sdr/calendar/connections',
    campaigns: '/prospect-sdr/campaigns',
    overview: '/prospect-sdr/dashboard/overview',
    observability: '/prospect-sdr/observability/executions',
  },
  whatsappFlow: {
    instances: {
      collection: '/whatsapp-flow/instances',
      item: (id: string) => `/whatsapp-flow/instances/${segment(id)}`,
      action: (id: string, action: string) => `/whatsapp-flow/instances/${segment(id)}/${segment(action)}`,
    },
    channels: {
      collection: '/whatsapp-flow/channels',
      item: (id: string) => `/whatsapp-flow/channels/${segment(id)}`,
      action: (id: string, action: string) => `/whatsapp-flow/channels/${segment(id)}/${segment(action)}`,
    },
    bindings: {
      collection: '/whatsapp-flow/bindings',
      item: (id: string) => `/whatsapp-flow/bindings/${segment(id)}`,
      action: (id: string, action: string) => `/whatsapp-flow/bindings/${segment(id)}/${segment(action)}`,
    },
    rules: {
      collection: '/whatsapp-flow/rules',
      item: (id: string) => `/whatsapp-flow/rules/${segment(id)}`,
      action: (id: string, action: string) => `/whatsapp-flow/rules/${segment(id)}/${segment(action)}`,
    },
    templates: {
      collection: '/whatsapp-flow/templates',
      item: (id: string) => `/whatsapp-flow/templates/${segment(id)}`,
      action: (id: string, action: string) => `/whatsapp-flow/templates/${segment(id)}/${segment(action)}`,
      connections: (id: string) => `/whatsapp-flow/templates/${segment(id)}/connections`,
      connection: (id: string, bindingId: string) => `/whatsapp-flow/templates/${segment(id)}/connections/${segment(bindingId)}`,
    },
    configuration: '/whatsapp-flow/configuration',
    configurationResources: {
      collection: (kind: string) => `/whatsapp-flow/configuration-resources/${segment(kind)}`,
      item: (kind: string, id: string) => `/whatsapp-flow/configuration-resources/${segment(kind)}/${segment(id)}`,
    },
    connections: {
      collection: '/whatsapp-flow/connections',
      item: (id: string) => `/whatsapp-flow/connections/${segment(id)}`,
      configuration: (id: string) => `/whatsapp-flow/connections/${segment(id)}/configuration`,
      readiness: (id: string) => `/whatsapp-flow/connections/${segment(id)}/readiness`,
      metrics: (id: string) => `/whatsapp-flow/connections/${segment(id)}/metrics`,
      executions: (id: string) => `/whatsapp-flow/connections/${segment(id)}/executions`,
      history: (id: string) => `/whatsapp-flow/connections/${segment(id)}/history`,
      operationOptions: (id: string) => `/whatsapp-flow/connections/${segment(id)}/operation-options`,
      action: (id: string, action: string) => `/whatsapp-flow/connections/${segment(id)}/${segment(action)}`,
    },
    syncConnections: '/whatsapp-flow/connections/sync',
    simulations: '/whatsapp-flow/simulations',
    executions: '/whatsapp-flow/executions',
    events: '/whatsapp-flow/events',
    stream: '/whatsapp-flow/stream',
    diagnostics: {
      connections: '/whatsapp-flow/diagnostics/connections',
      execution: (messageId: string) => `/whatsapp-flow/diagnostics/executions/${segment(messageId)}`,
      reprocess: (messageId: string) => `/whatsapp-flow/diagnostics/reprocess/${segment(messageId)}`,
    },
    dashboard: {
      overview: '/whatsapp-flow/dashboard/overview',
      routing: '/whatsapp-flow/dashboard/routing',
      templates: '/whatsapp-flow/dashboard/templates',
      revenue: '/whatsapp-flow/dashboard/revenue',
    },
  },
  audit: {
    collection: '/audit',
    item: (eventId: string) => `/audit/${segment(eventId)}`,
  },
} as const

export function administrativeItemRoute(collectionPath: string, resourceId: string) {
  return `${collectionPath.replace(/\/+$/, '')}/${segment(resourceId)}`
}

export const administrativeCollectionRoutes = [
  adminRoutes.tenants.collection,
  adminRoutes.environments.collection,
  adminRoutes.users.collection,
  adminRoutes.roles.collection,
  adminRoutes.catalog.products,
  adminRoutes.agents.collection,
  adminRoutes.tokens.budgets,
  adminRoutes.providers.collection,
  adminRoutes.apiRoutes.collection,
  adminRoutes.configurations.collection,
  adminRoutes.featureFlags.collection,
  adminRoutes.tryOn.sessions,
  adminRoutes.apparelTryOn.garments,
  adminRoutes.apparelTryOn.generations,
  adminRoutes.consents.collection,
  adminRoutes.audit.collection,
  adminRoutes.prospectSDR.leads,
  adminRoutes.whatsappFlow.instances.collection,
] as const
